/**
 * UpdateBanner Component
 * Displays a notification banner when a new app version is available
 * 
 * Features:
 * - Fixed position at top of screen
 * - Mobile responsive design
 * - Refresh and dismiss buttons
 * - Accessible (ARIA attributes)
 * - Animated entrance/exit
 */

import React, { useState, useEffect } from 'react';
import { RefreshCw, X, Download } from 'lucide-react';
import { useUpdateAvailable } from '../../contexts/VersionContext';

const UpdateBanner = ({ 
  position = 'top',
  className = '',
  showDismiss = true,
  autoRefreshDelay = null // If set, auto-refresh after this many seconds
}) => {
  const { updateAvailable, refresh, dismissUpdate } = useUpdateAvailable();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [countdown, setCountdown] = useState(null);

  // Animate in when update becomes available
  useEffect(() => {
    if (updateAvailable) {
      // Small delay for animation
      const timer = setTimeout(() => setIsVisible(true), 100);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [updateAvailable]);

  // Auto-refresh countdown
  useEffect(() => {
    if (!updateAvailable || !autoRefreshDelay) return;

    setCountdown(autoRefreshDelay);
    
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          handleRefresh();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [updateAvailable, autoRefreshDelay]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    // Small delay so user sees the loading state
    setTimeout(() => {
      refresh(true);
    }, 300);
  };

  const handleDismiss = () => {
    setIsVisible(false);
    // Wait for animation to complete before actually dismissing
    setTimeout(() => {
      dismissUpdate();
    }, 300);
  };

  if (!updateAvailable) {
    return null;
  }

  const positionClasses = position === 'top' 
    ? 'top-0 left-0 right-0' 
    : 'bottom-0 left-0 right-0';

  const animationClasses = isVisible
    ? 'translate-y-0 opacity-100'
    : position === 'top'
      ? '-translate-y-full opacity-0'
      : 'translate-y-full opacity-0';

  return (
    <div
      role="alert"
      aria-live="polite"
      aria-label="Application update available"
      className={`
        fixed ${positionClasses} z-50
        transform transition-all duration-300 ease-out
        ${animationClasses}
        ${className}
      `}
    >
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between flex-wrap gap-2">
            {/* Message */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="flex-shrink-0">
                <Download className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
              </div>
              <p className="text-sm sm:text-base font-medium truncate">
                <span className="hidden sm:inline">A new version is available. </span>
                <span className="sm:hidden">Update available. </span>
                {countdown !== null && (
                  <span className="text-blue-200">
                    Auto-refresh in {countdown}s
                  </span>
                )}
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className={`
                  inline-flex items-center gap-2 px-4 py-2
                  bg-white text-blue-600 rounded-lg
                  font-medium text-sm
                  hover:bg-blue-50 active:bg-blue-100
                  focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600
                  transition-colors duration-150
                  disabled:opacity-70 disabled:cursor-not-allowed
                  min-h-[44px] min-w-[44px]
                `}
                aria-label="Refresh to update"
              >
                <RefreshCw 
                  className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} 
                  aria-hidden="true" 
                />
                <span className="hidden sm:inline">
                  {isRefreshing ? 'Refreshing...' : 'Refresh Now'}
                </span>
                <span className="sm:hidden">
                  {isRefreshing ? '...' : 'Update'}
                </span>
              </button>

              {/* Dismiss Button */}
              {showDismiss && (
                <button
                  onClick={handleDismiss}
                  className={`
                    p-2 rounded-lg
                    text-white/80 hover:text-white hover:bg-white/10
                    focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600
                    transition-colors duration-150
                    min-h-[44px] min-w-[44px]
                    flex items-center justify-center
                  `}
                  aria-label="Dismiss update notification"
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Toast-style update notification for mobile
 */
export const UpdateToast = ({ className = '' }) => {
  const { updateAvailable, refresh, dismissUpdate } = useUpdateAvailable();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (updateAvailable) {
      const timer = setTimeout(() => setIsVisible(true), 100);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [updateAvailable]);

  if (!updateAvailable) {
    return null;
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      className={`
        fixed bottom-4 left-4 right-4 z-50
        transform transition-all duration-300 ease-out
        ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}
        ${className}
      `}
    >
      <div className="bg-gray-900 text-white rounded-xl shadow-2xl overflow-hidden">
        <div className="p-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
              <Download className="h-5 w-5" aria-hidden="true" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">Update Available</p>
              <p className="text-gray-400 text-xs">Tap to refresh and get the latest version</p>
            </div>
          </div>
          
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => refresh(true)}
              className="flex-1 bg-blue-500 text-white py-2.5 px-4 rounded-lg font-medium text-sm hover:bg-blue-600 active:bg-blue-700 transition-colors min-h-[44px]"
            >
              Refresh Now
            </button>
            <button
              onClick={() => {
                setIsVisible(false);
                setTimeout(dismissUpdate, 300);
              }}
              className="px-4 py-2.5 text-gray-400 hover:text-white transition-colors min-h-[44px]"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateBanner;
